import React, { useCallback, useMemo } from 'react'
import {
  buildQuotationTimelineModel,
  formatQuotationDisplayDate,
  minQuotationWindowOffsetSpan,
  resolveQuotationTimelineScale,
  windowDatesFromOffsets,
  windowOffsetsFromDates
} from './rubroQuotationTimelineUtils.ts'
import styles from './RubroQuotationTimeline.module.scss'
import ProjectAutofillInfoNote from './ProjectAutofillInfoNote.tsx'

type Props = {
  projectStartDate: string
  projectEndDate: string
  windowStartDate: string
  windowEndDate: string
  disabled?: boolean
  onWindowDatesChange?: (startDate: string, endDate: string) => void
}

function handleLabelPositionStyle(percent: number): React.CSSProperties {
  if (percent <= 12) {
    return { left: `${percent}%`, transform: 'translateX(0)' }
  }
  if (percent >= 88) {
    return { left: `${percent}%`, transform: 'translateX(-100%)' }
  }
  return { left: `${percent}%`, transform: 'translateX(-50%)' }
}

const RubroQuotationTimeline: React.FC<Props> = ({
  projectStartDate,
  projectEndDate,
  windowStartDate,
  windowEndDate,
  disabled = false,
  onWindowDatesChange
}) => {
  const scale = useMemo(
    () =>
      resolveQuotationTimelineScale(
        projectStartDate,
        projectEndDate,
        windowStartDate,
        windowEndDate
      ),
    [projectStartDate, projectEndDate, windowStartDate, windowEndDate]
  )

  const offsets = useMemo(() => {
    if (!scale) return { startOffset: 0, endOffset: 0 }
    return windowOffsetsFromDates(scale, windowStartDate, windowEndDate)
  }, [scale, windowStartDate, windowEndDate])

  const model = useMemo(
    () =>
      buildQuotationTimelineModel({
        projectStartDate,
        projectEndDate,
        windowStartDate,
        windowEndDate
      }),
    [projectStartDate, projectEndDate, windowStartDate, windowEndDate]
  )

  const segmentStyle = useMemo(() => {
    if (!scale) {
      return { prepLeft: 0, prepWidth: 0, windowLeft: 0, windowWidth: 100 }
    }
    if (scale.maxOffset <= 0) {
      return { prepLeft: 0, prepWidth: 0, windowLeft: 0, windowWidth: 100 }
    }
    const total = scale.maxOffset
    const prepWidth = (offsets.startOffset / total) * 100
    const windowLeft = prepWidth
    const span = Math.max(offsets.endOffset - offsets.startOffset, 0)
    const windowWidth = Math.max((span / total) * 100, span === 0 ? 1.5 : 2)
    return { prepLeft: 0, prepWidth, windowLeft, windowWidth }
  }, [scale, offsets])

  const emitOffsets = useCallback(
    (startOffset: number, endOffset: number) => {
      if (!scale || !onWindowDatesChange) return
      const next = windowDatesFromOffsets(scale, startOffset, endOffset)
      onWindowDatesChange(next.startDate, next.endDate)
    },
    [scale, onWindowDatesChange]
  )

  const interactive = !!scale && !!onWindowDatesChange && !disabled

  const minOffsetSpan = scale ? minQuotationWindowOffsetSpan(scale) : 0

  const handleStartSliderChange = useCallback(
    (nextStart: number) => {
      if (!scale) return
      const maxStart = Math.max(0, offsets.endOffset - minOffsetSpan)
      const clampedStart = Math.max(0, Math.min(nextStart, maxStart))
      emitOffsets(clampedStart, offsets.endOffset)
    },
    [scale, minOffsetSpan, offsets.endOffset, emitOffsets]
  )

  const handleEndSliderChange = useCallback(
    (nextEnd: number) => {
      if (!scale) return
      const minEnd = Math.min(scale.maxOffset, offsets.startOffset + minOffsetSpan)
      const clampedEnd = Math.min(scale.maxOffset, Math.max(nextEnd, minEnd))
      emitOffsets(offsets.startOffset, clampedEnd)
    },
    [scale, minOffsetSpan, offsets.startOffset, emitOffsets]
  )

  const thumbLeftPercent = useCallback(
    (offset: number) => {
      if (!scale || scale.maxOffset <= 0) return 0
      return (offset / scale.maxOffset) * 100
    },
    [scale]
  )

  const startLabelLeft = thumbLeftPercent(offsets.startOffset)
  const endLabelLeft = thumbLeftPercent(offsets.endOffset)
  const handleLabelsOverlap = Math.abs(startLabelLeft - endLabelLeft) < 12
  const showWindowMarkers = !!scale

  return (
    <div className={styles.wrap}>
      <p className={styles.lead}>
        Primero la obra se publica y los cotizantes pueden revisar la documentación. Después, en el período
        resaltado, queda abierto el envío de presupuestos para este rubro.
        {interactive
          ? ' Usá los controles Apertura y Cierre sobre la barra para definir la ventana de cotización de este rubro.'
          : null}
      </p>
      {model.hasTrack && scale ? (
        <>
          <div className={styles.trackWrap}>
            <div
              className={styles.track}
              role={interactive ? 'group' : 'img'}
              aria-label={interactive ? undefined : model.ariaSummary}
            >
              <div className={styles.trackBase} aria-hidden />
              {segmentStyle.prepWidth > 0.5 ? (
                <div
                  className={styles.prep}
                  style={{ left: `${segmentStyle.prepLeft}%`, width: `${segmentStyle.prepWidth}%` }}
                  aria-hidden
                />
              ) : null}
              <div
                className={styles.window}
                style={{ left: `${segmentStyle.windowLeft}%`, width: `${segmentStyle.windowWidth}%` }}
                aria-hidden
              />
            </div>
            {showWindowMarkers ? (
              <div className={styles.handleLabels} aria-hidden>
                <span
                  className={`${styles.handleLabel} ${styles.handleLabelStart}`}
                  style={handleLabelPositionStyle(startLabelLeft)}
                >
                  Apertura
                </span>
                <span
                  className={`${styles.handleLabel} ${styles.handleLabelEnd} ${
                    handleLabelsOverlap ? styles.handleLabelEndStacked : ''
                  }`}
                  style={handleLabelPositionStyle(endLabelLeft)}
                >
                  Cierre
                </span>
              </div>
            ) : null}
            {interactive ? (
              <div className={styles.sliderLayer}>
                <input
                  type="range"
                  className={`${styles.range} ${styles.rangeStart}`}
                  min={0}
                  max={scale.maxOffset}
                  step={1}
                  value={offsets.startOffset}
                  disabled={disabled}
                  aria-label="Apertura del envío de presupuestos"
                  aria-valuemin={0}
                  aria-valuemax={scale.maxOffset}
                  aria-valuenow={offsets.startOffset}
                  aria-valuetext={formatQuotationDisplayDate(windowStartDate)}
                  onChange={(e) => handleStartSliderChange(Number(e.target.value))}
                />
                <input
                  type="range"
                  className={`${styles.range} ${styles.rangeEnd}`}
                  min={0}
                  max={scale.maxOffset}
                  step={1}
                  value={offsets.endOffset}
                  disabled={disabled}
                  aria-label="Cierre del envío de presupuestos"
                  aria-valuemin={0}
                  aria-valuemax={scale.maxOffset}
                  aria-valuenow={offsets.endOffset}
                  aria-valuetext={formatQuotationDisplayDate(windowEndDate)}
                  onChange={(e) => handleEndSliderChange(Number(e.target.value))}
                />
              </div>
            ) : showWindowMarkers ? (
              <div className={styles.readonlyMarkerLayer} aria-hidden>
                <span
                  className={`${styles.readonlyMarker} ${styles.readonlyMarkerStart}`}
                  style={handleLabelPositionStyle(startLabelLeft)}
                />
                <span
                  className={`${styles.readonlyMarker} ${styles.readonlyMarkerEnd}`}
                  style={handleLabelPositionStyle(endLabelLeft)}
                />
              </div>
            ) : null}
          </div>
          <div className={styles.timelineRefsBlock}>
            <ol
              className={styles.timelineRefs}
              aria-label="Fechas del calendario de la obra y la ventana de cotización"
            >
              <li className={styles.timelineRefItem}>
                <span className={styles.timelineRefLabel}>Inicio de la publicación</span>
                <time className={styles.timelineRefDate} dateTime={projectStartDate || undefined}>
                  {model.projectStartLabel || formatQuotationDisplayDate(projectStartDate) || '—'}
                </time>
              </li>
              <li className={styles.timelineRefItem}>
                <span className={styles.timelineRefLabel}>Apertura de cotización</span>
                <time className={styles.timelineRefDate} dateTime={windowStartDate || undefined}>
                  {model.windowStartLabel || '—'}
                </time>
              </li>
              <li className={styles.timelineRefItem}>
                <span className={styles.timelineRefLabel}>Cierre de cotización</span>
                <time className={styles.timelineRefDate} dateTime={windowEndDate || undefined}>
                  {model.windowEndLabel || '—'}
                </time>
              </li>
              <li className={styles.timelineRefItem}>
                <span className={styles.timelineRefLabel}>Fin de la publicación</span>
                <time className={styles.timelineRefDate} dateTime={projectEndDate || undefined}>
                  {formatQuotationDisplayDate(projectEndDate) || '—'}
                </time>
              </li>
            </ol>
          </div>
          <div className={styles.trackLegend} role="note" aria-label="Significado de los colores en la barra">
            <span className={styles.trackLegendHeading}>Colores en la barra</span>
            <div className={styles.legendItems}>
              {model.showPrep ? (
                <div className={styles.legendItem}>
                  <span className={`${styles.legendBar} ${styles.legendBarPrep}`} aria-hidden />
                  <span className={styles.legendCopy}>
                    <span className={styles.legendTitle}>Revisión de la documentación</span>
                    {model.prepDays > 0 ? (
                      <span className={styles.legendDetail}>
                        ~{model.prepDays} día{model.prepDays === 1 ? '' : 's'} para revisar antes de abrir la
                        ventana
                      </span>
                    ) : (
                      <span className={styles.legendDetail}>Sin envío de presupuestos</span>
                    )}
                  </span>
                </div>
              ) : null}
              <div className={styles.legendItem}>
                <span className={`${styles.legendBar} ${styles.legendBarWindow}`} aria-hidden />
                <span className={styles.legendCopy}>
                  <span className={styles.legendTitle}>Envío de presupuestos</span>
                  <span className={styles.legendDetail}>
                    {model.windowDays} día{model.windowDays === 1 ? '' : 's'} de duración
                  </span>
                </span>
              </div>
            </div>
          </div>
          {interactive && model.showSameDayWarning ? (
            <ProjectAutofillInfoNote tone="caution" className={styles.warn}>
              La ventana abre el mismo día que la publicación del proyecto. Conviene dejar algunos días para que
              revisen la documentación antes de enviar presupuestos.
            </ProjectAutofillInfoNote>
          ) : interactive && model.showTightPrepWarning ? (
            <ProjectAutofillInfoNote tone="caution" className={styles.warn}>
              Hay pocos días entre la publicación y la apertura de la ventana. Los cotizantes podrían necesitar más
              tiempo para revisar la documentación.
            </ProjectAutofillInfoNote>
          ) : null}
        </>
      ) : (
        <div className={styles.placeholder} role="img" aria-label={model.ariaSummary} />
      )}
    </div>
  )
}

export default RubroQuotationTimeline
